import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";
import { fetchImageAsBase64 } from "../../../../../../../utils/imageUtils";


//APP
import { imgLogo } from "../../../../../../../utils/base64-Imgs";
import { typeEstruturaTodasAtvs } from "./estrutura_todas_atvs";
import { borderBottomLeftRadius } from "html2canvas/dist/types/css/property-descriptors/border-radius";

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

export interface HeaderDef {
    logo: string;
    titulo: string;
    nome_arquivo?: string;
    razaoSocial: string;
    cliente?: string;
    safra?: string;
    cultura?: string;
    autor?: string;
}

export const gerarDocDefinition = (
    dados: typeEstruturaTodasAtvs[],
    incluir_insumos: boolean,
    incluir_patrimonios: boolean,
    incluir_referencias: boolean,
    posicao_legenda: 'Por célula' | 'Por tag' | 'Por referência',
    headerInfo?: HeaderDef
) => {
    const content: any[] = [];

    // Coleta todas as referências únicas
    const uniqueReferences: string[] = [];
    if (incluir_referencias && posicao_legenda === 'Por referência') {
        dados.forEach((propriedade) => {
            propriedade.lista_atividades_por_talhao.forEach((talhao) => {
                talhao.lista_atividades.forEach((atv) => {
                    if (atv.nome_atv_vinculada && atv.nome_atv_vinculada.trim() !== '') {
                        if (!uniqueReferences.includes(atv.nome_atv_vinculada)) {
                            uniqueReferences.push(atv.nome_atv_vinculada);
                        }
                    }
                });
            });
        });
    }

    // Header Principal (Somente Primeira Página)
    content.push({
        stack: [
            {
                image: headerInfo?.logo || 'imgLogoAlias',
                width: 80,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                text: `${headerInfo?.titulo}`,
                style: 'headerMain',
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 760,
                        h: 4,
                        linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                    }
                ],
            },
            {
                stack: [
                    {
                        text: `${headerInfo?.razaoSocial}`,
                        fontSize: 8,
                        bold: true,
                        color: '#4D4D4D',
                        alignment: 'center'
                    },
                    ...(headerInfo?.cliente ? [{
                        margin: [0, 10, 0, 0],
                        table: {
                            widths: ['*', '30%', '30%'],
                            body: [
                                [
                                    {
                                        text: [
                                            { text: 'CLIENTE\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.cliente || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    },
                                    {
                                        text: [
                                            { text: 'SAFRA\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.safra || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    },
                                    {
                                        text: [
                                            { text: 'CULTURA\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.cultura || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    }
                                ]
                            ]
                        },
                        layout: {
                            hLineWidth: () => 1,
                            vLineWidth: () => 1,
                            hLineColor: () => '#7b7b7b',
                            vLineColor: () => '#7b7b7b'
                        }
                    }] : [])
                ],
                margin: [0, 5, 0, 20]
            }
        ],
        margin: [0, 0, 0, 10]
    });

    // Legenda de referências na primeira página
    if (incluir_referencias && posicao_legenda === 'Por referência' && uniqueReferences.length > 0) {
        content.push({
            margin: [0, 0, 0, 15],
            table: {
                widths: ['*'],
                body: [
                    [
                        {
                            stack: [
                                { text: 'ATIVIDADES VINCULADAS', bold: true, fontSize: 8.5, color: '#137333', margin: [0, 0, 0, 4] },
                                {
                                    columns: uniqueReferences.map((refName, idx) => ({
                                        width: 'auto',
                                        text: `[${idx + 1}] ${refName}`,
                                        fontSize: 8,
                                        bold: true,
                                        color: '#4D4D4D',
                                        margin: [0, 0, 15, 0]
                                    }))
                                }
                            ],
                            fillColor: '#f4fbf7',
                            margin: [8, 6, 8, 6]
                        }
                    ]
                ]
            },
            layout: {
                hLineWidth: () => 1,
                vLineWidth: () => 1,
                hLineColor: () => '#5da57d',
                vLineColor: () => '#5da57d'
            }
        });
    }

    dados.forEach((propriedade) => {
        // Header Propriedade
        content.push({
            table: {
                widths: ['*'],
                body: [
                    [
                        {
                            text: propriedade.nome_propriedade || "PROPRIEDADE NÃO INFORMADA",
                            style: 'headerPropriedade',
                            alignment: 'center',
                            fillColor: '#5da57d',
                            color: 'white',
                            bold: true,
                            margin: [0, 5, 0, 5]
                        }
                    ]
                ]
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 0]
        });

        propriedade.lista_atividades_por_talhao.forEach((talhao) => {
            // Header Talhao
            content.push({
                table: {
                    widths: ['*'],
                    body: [
                        [
                            {
                                text: talhao.nome_talhao || "TALHÃO NÃO INFORMADO",
                                style: 'headerTalhao',
                                alignment: 'center',
                                fillColor: '#fff',
                                color: '#165c13',
                                bold: true,
                                margin: [2, 2, 2, 2],
                                border: [true, true, true, true]
                            }
                        ]
                    ]
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#165c13',
                    vLineColor: () => '#165c13'
                },
                margin: [0, 0, 0, 0]
            });

            let globalAtvIndex = 0; // Para controle de zebra por bloco lógico
            const corHeader = '#0e743f';
            const bodyAtividades: any[] = [
                // Table Header Atividades
                [
                    { text: 'Atividade', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' },
                    { text: 'Operação', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' },
                    { text: 'Datas', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' },
                    { text: 'Área', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' },
                    { text: 'Área executada', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' },
                    { text: 'Concluída', style: 'tableHeader', alignment: 'center', fillColor: corHeader, color: 'white' }
                ]
            ];

            talhao.lista_atividades.forEach((atv) => {

                const formatarData = (dInicio: string | null, dFim: string | null) => {
                    if (!dInicio && !dFim) return '-';
                    const i = dInicio ? dayjs(dInicio).format("DD/MM/YY") : '?';
                    const f = dFim ? dayjs(dFim).format("DD/MM/YY") : '?';
                    if (i === f) return i;
                    return `${i} - ${f}`;
                };

                const dataStr = formatarData(atv.data_inicio, atv.data_final);
                const descLen = (atv.descricao || '-').length;
                const extraLines = Math.max(Math.ceil(descLen / 34) - 1, 0);
                const offset = extraLines * 6;

                const zebraColor = (globalAtvIndex % 2 === 0) ? null : '#e5f1e1';

                const chips: any[] = [];
                let diasChipTop: any = null;
                const formatarChipValor = (num: number) => {
                    const absVal = Math.abs(num);
                    const prefixo = num < 0 ? '-' : '';
                    const numStr = absVal < 10 ? `0${absVal}` : String(absVal);
                    return `${prefixo}${numStr}`;
                };

                if (atv.dias !== undefined && atv.dias !== null && !isNaN(atv.dias) && Number(atv.dias) !== 0) {
                    const hasVinculada = incluir_referencias && atv.nome_atv_vinculada && atv.nome_atv_vinculada.trim() !== '';

                    if (hasVinculada && posicao_legenda === 'Por tag') {
                        diasChipTop = {
                            width: 'auto',
                            table: {
                                widths: ['auto'],
                                body: [[{
                                    text: `(${Number(atv.dias) > 0 ? "+" : ""} ${atv.dias} dias) de ${atv.nome_atv_vinculada.trim().charAt(0).toUpperCase() + atv.nome_atv_vinculada.trim().slice(1).toLowerCase()}`,
                                    fontSize: 8.5,
                                    bold: true,
                                    color: Number(atv.dias) >= 0 ? '#51c11a' : '#1a73e8',
                                    fillColor: Number(atv.dias) >= 0 ? '#effedf' : '#e8f0fe',
                                    margin: [4, 1.5, 4, 1.5],
                                    border: [true, true, true, true]
                                }]]
                            },
                            layout: {
                                hLineWidth: () => 1,
                                vLineWidth: () => 1,
                                hLineColor: () => Number(atv.dias) >= 0 ? '#137333' : '#1a73e8',
                                vLineColor: () => Number(atv.dias) >= 0 ? '#137333' : '#1a73e8'
                            }
                        };
                    } else {
                        let chipText = `${Number(atv.dias) > 0 ? "+" : ""} ${atv.dias} D`;
                        if (hasVinculada && posicao_legenda === 'Por referência') {
                            const refIdx = uniqueReferences.indexOf(atv.nome_atv_vinculada);
                            if (refIdx !== -1) {
                                chipText = `${Number(atv.dias) > 0 ? "+" : ""} ${atv.dias} D [${refIdx + 1}]`;
                            }
                        }
                        chips.push({
                            width: 'auto',
                            table: {
                                widths: ['auto'],
                                body: [[{
                                    text: chipText,
                                    fontSize: 7.5,
                                    bold: true,
                                    color: Number(atv.dias) >= 0 ? '#51c11a' : '#1a73e8',
                                    fillColor: Number(atv.dias) >= 0 ? '#effedf' : '#e8f0fe',
                                    margin: [3, 0.5, 3, 0.5],
                                    border: [true, true, true, true]
                                }]]
                            },
                            layout: {
                                hLineWidth: () => 1,
                                vLineWidth: () => 1,
                                hLineColor: () => Number(atv.dias) >= 0 ? '#137333' : '#1a73e8',
                                vLineColor: () => Number(atv.dias) >= 0 ? '#137333' : '#1a73e8'
                            }
                        });
                    }
                }

                if (atv.dias_apos_plantio !== undefined && atv.dias_apos_plantio !== null && !isNaN(atv.dias_apos_plantio) && Number(atv.dias_apos_plantio) > 0) {
                    chips.push({
                        width: 'auto',
                        table: {
                            widths: ['auto'],
                            body: [[{
                                text: `${formatarChipValor(atv.dias_apos_plantio)} DAP`,
                                fontSize: 7.5,
                                bold: true,
                                color: '#137333',
                                margin: [3, 0.5, 3, 0.5],
                                border: [true, true, true, true],
                                fillColor: '#e6f4ea'
                            }]]
                        },
                        layout: {
                            hLineWidth: () => 1,
                            vLineWidth: () => 1,
                            hLineColor: () => '#137333',
                            vLineColor: () => '#137333'
                        }
                    });
                }

                if (atv.dias_apos_emergencia !== undefined && atv.dias_apos_emergencia !== null && !isNaN(atv.dias_apos_emergencia) && Number(atv.dias_apos_emergencia) > 0) {
                    chips.push({
                        width: 'auto',
                        table: {
                            widths: ['auto'],
                            body: [[{
                                text: `${formatarChipValor(atv.dias_apos_emergencia)} DAE`,
                                fontSize: 7.5,
                                bold: true,
                                color: '#137333',
                                fillColor: '#fff',
                                margin: [3, 0.5, 3, 0.5],
                                border: [true, true, true, true]
                            }]]
                        },
                        layout: {
                            hLineWidth: () => 1,
                            vLineWidth: () => 1,
                            hLineColor: () => '#137333',
                            vLineColor: () => '#137333'
                        }
                    });
                }

                bodyAtividades.push([
                    {
                        stack: [
                            ...(diasChipTop ? [{
                                columns: [
                                    { width: '*', text: '' },
                                    diasChipTop,
                                    { width: '*', text: '' }
                                ],
                                columnGap: 4,
                                margin: [0, 0, 0, 6]
                            }] : []),
                            { text: atv.descricao || '-', alignment: 'center' },
                            ...(chips.length > 0 ? [{
                                columns: [
                                    { width: '*', text: '' },
                                    ...chips,
                                    { width: '*', text: '' }
                                ],
                                columnGap: 4,
                                margin: [0, 6, 0, 0]
                            }] : []),
                            ...(incluir_referencias && posicao_legenda === 'Por célula' && atv.nome_atv_vinculada && atv.nome_atv_vinculada.trim() !== '' ? [{
                                text: `* ${atv.nome_atv_vinculada}`,
                                fontSize: 6.5,
                                bold: true,
                                color: Number(atv.dias) >= 0 ? '#51c11a' : '#1a73e8',
                                margin: [0, 4, 0, 0],
                                alignment: 'center'
                            }] : [])
                        ],
                        margin: [0, 1, 0, 8],
                        alignment: 'center',
                        fillColor: zebraColor
                    },
                    { text: atv.tipo_operacao || '-', margin: [0, 8 + offset, 0, 8], alignment: 'center', fillColor: zebraColor },
                    { text: dataStr, margin: [0, 8 + offset, 0, 8], alignment: 'center', fillColor: zebraColor },
                    { text: `${atv.area} ha (remonte: ${atv.porcentagem_remonte}%)`, margin: [0, 8 + offset, 0, 8], alignment: 'center', fillColor: zebraColor },
                    { text: `${atv.area_executada} ha (${atv.porcentagem_executada}%)`, margin: [0, 8 + offset, 0, 8], alignment: 'center', fillColor: zebraColor },
                    {
                        text: atv.concluido ? 'SIM' : 'NÃO',
                        fillColor: atv.concluido ? '#01c16c' : zebraColor,
                        color: atv.concluido ? '#fff' : '#000',
                        bold: true,
                        alignment: 'center',
                        margin: [0, 8 + offset, 0, 8]
                    }
                ]);

                const temObservacao = !!(atv.observacao && atv.observacao.trim() !== '');

                if (incluir_insumos || incluir_patrimonios || temObservacao) {

                    const subTables: any[] = [];

                    if (temObservacao) {
                        subTables.push({
                            table: {
                                widths: ['*'],
                                body: [[{
                                    text: 'Observação',
                                    bold: true,
                                    fontSize: 10,
                                    margin: [5, 2, 0, 2],
                                    alignment: 'left'
                                }]]
                            },
                            layout: 'noBorders',
                            margin: [0, 5, 0, 2]
                        });

                        subTables.push({
                            text: atv.observacao,
                            color: '#4D4D4D',
                            fontSize: 9,
                            margin: [25, 0, 0, 10],
                            alignment: 'left'
                        });
                    }

                    if (incluir_insumos) {
                        subTables.push({
                            table: {
                                widths: ['*'],
                                body: [[{
                                    text: 'Insumos Planejados',
                                    // fillColor: '#f8f9fa',
                                    bold: true,
                                    fontSize: 10,
                                    margin: [5, 2, 0, 2],
                                    alignment: 'left'
                                }]]
                            },
                            layout: 'noBorders',
                            margin: [0, 5, 0, 2]
                        });

                        if (atv.insumos && atv.insumos.length > 0) {
                            const listaInsumos = atv.insumos.map((insu: any) => {
                                const detalhes = () => {
                                    switch (insu?.cod_tipo) {
                                        case 8:
                                            return `\nTipo: ${insu.nome_tipo || '-'} | Doses: ${insu.doses_inoculante || '-'} | Dosagem: ${insu.dosagem || 0} ${insu.nome_medida || ''}/ha | Qtde: ${insu.quantidade_insumo || 0} ${insu.observacao ? `| Obs: ${insu.observacao}` : ''}`;

                                        default:
                                            return `\nTipo: ${insu.nome_tipo || '-'} | Dosagem: ${insu.dosagem || 0} ${insu.nome_medida || ''}/ha | ${atv.atv_is_pulverizacao ? 'Total (área)' : 'Qtde'}: ${insu.quantidade_insumo || 0} ${(atv.atv_is_pulverizacao && insu.no_tanque > 0) ? `| No Tanque: ${insu.no_tanque || 0} L` : ''} ${insu.observacao ? `| Obs: ${insu.observacao}` : ''}`;
                                    }
                                }
                                return {
                                    text: [
                                        { text: `${insu.insumo_nome || '-'}`, bold: true, fontSize: 10, color: '#2E2E2E' },
                                        { text: detalhes(), fontSize: 9, color: '#4D4D4D' }
                                    ],
                                    margin: [0, 3, 0, 3]
                                };
                            });

                            subTables.push({
                                ul: listaInsumos,
                                margin: [25, 2, 0, 8],
                                alignment: 'left'
                            });
                        } else {
                            subTables.push({
                                text: 'Não possui insumos cadastrados',
                                italics: true,
                                color: '#4D4D4D',
                                fontSize: 9,
                                margin: [25, 0, 0, 10],
                                alignment: 'left'
                            });
                        }
                    }

                    if (incluir_patrimonios) {
                        subTables.push({
                            table: {
                                widths: ['*'],
                                body: [[{
                                    text: 'Patrimônios Planejados',
                                    // fillColor: '#f8f9fa',
                                    bold: true,
                                    fontSize: 10,
                                    margin: [5, 2, 0, 2],
                                    alignment: 'left'
                                }]]
                            },
                            layout: 'noBorders',
                            margin: [0, 2, 0, 2]
                        });

                        if (atv.patrimonios && atv.patrimonios.length > 0) {
                            const listaPatrimonios = atv.patrimonios.map((pat: any) => {
                                return {
                                    text: [
                                        { text: `${pat.patrimonio_nome || pat.nome || '-'}`, bold: true, fontSize: 10, color: '#2E2E2E' },
                                        { text: `\nCapacidade do tanque: ${pat?.capacidade_tanque ?? ''} L | Vazão: ${pat?.vazao ?? ''} L | Área por tanque: ${Number(pat?.capacidade_tanque || 0) > 0 && Number(pat?.vazao || 0) > 0 ? Number(pat?.capacidade_tanque / pat?.vazao).toFixed(2) : ''} L/ha`, fontSize: 9, color: '#4D4D4D' }
                                    ],
                                    margin: [0, 3, 0, 3]
                                };
                            });

                            subTables.push({
                                ul: listaPatrimonios,
                                margin: [25, 2, 0, 8],
                                alignment: 'left'
                            });
                        } else {
                            subTables.push({
                                text: 'Não possui patrimônios cadastrados',
                                italics: true,
                                color: '#4D4D4D',
                                fontSize: 9,
                                margin: [25, 0, 0, 10],
                                alignment: 'left'
                            });
                        }
                    }

                    if (subTables.length > 0) {
                        bodyAtividades.push([
                            {
                                colSpan: 6,
                                stack: subTables,
                                margin: [40, 0, 40, 5],
                                fillColor: zebraColor
                            },
                            {}, {}, {}, {}, {}
                        ]);
                    }
                }

                globalAtvIndex++;
            });

            content.push({
                table: {
                    widths: ['30%', '15%', '18%', '10%', '15%', '12%'],
                    body: bodyAtividades
                },
                layout: {
                    fillColor: function (rowIndex: number, node: any, columnIndex: number) {
                        if (rowIndex === 0) return '#a6a6a6'; // Header principal
                        return null;
                    }
                },
                margin: [0, 0, 0, 15]
            });
        });
    });

    return {
        info: {
            title: headerInfo?.nome_arquivo || headerInfo?.titulo || 'Relatório de atividades',
            author: headerInfo?.autor || '',
        },
        content: content,
        pageOrientation: 'landscape',
        footer: (currentPage: number, pageCount: number) => {
            const dataGeracao = dayjs().format("DD/MM/YYYY, HH:mm:ss");
            return {
                stack: [
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        canvas: [
                            {
                                type: 'rect',
                                x: 0,
                                y: 0,
                                w: 760,
                                h: 4,
                                linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                            }
                        ],
                        margin: [0, 0, 0, 5]
                    },
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        columns: [
                            {
                                width: '*',
                                stack: [
                                    {
                                        image: 'imgLogoAlias',
                                        width: 50,
                                        margin: [0, 3, 0, 0],
                                    },
                                ],
                                alignment: 'left',
                            },
                            {
                                width: '*',
                                stack: [
                                    {
                                        text: `Gerado por: ${headerInfo?.autor}`,
                                        bold: true,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                    {
                                        text: `${dataGeracao}`,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                ],
                                margin: [0, 3, 0, 0],
                                alignment: 'center',
                            },
                            {
                                width: '*',
                                text: `${currentPage} de ${pageCount}`,
                                alignment: 'right',
                                fontSize: 8,
                                margin: [0, 3, 0, 0],
                                color: '#4D4D4D'
                            },
                        ],
                    },
                    {
                        width: '*',
                        text: headerInfo?.razaoSocial ?? '',
                        alignment: 'left',
                        fontSize: 6,
                        margin: [0, 3, 0, 10],
                        color: '#4D4D4D'
                    },
                ],
                margin: [40, 0, 40, 0],
            };
        },
        images: {
            imgLogoAlias: imgLogo
        },
        styles: {
            headerMain: {
                fontSize: 20,
                bold: true,
                color: 'black'
            },
            headerPropriedade: {
                fontSize: 14,
                bold: true
            },
            headerTalhao: {
                fontSize: 12,
                bold: true
            },
            tableHeader: {
                bold: true,
                fontSize: 11,
                color: 'white',
                alignment: 'center'
            }
        },
        defaultStyle: {
            fontSize: 10,
            alignment: 'center'
        },
        pageMargins: [40, 40, 40, 60] // Restaurado margem superior padrão
    };
};

export const gerarPdfDataUrl = async (
    dados: typeEstruturaTodasAtvs[],
    incluir_insumos: boolean,
    incluir_patrimonios: boolean,
    incluir_referencias: boolean,
    posicao_legenda: 'Por célula' | 'Por tag' | 'Por referência',
    headerInfo?: HeaderDef
): Promise<string> => {
    // Se houver uma logo em formato de URL, converte para Base64 antes de gerar o PDF
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    return new Promise((resolve, reject) => {
        try {
            const docDefinition = gerarDocDefinition(dados, incluir_insumos, incluir_patrimonios, incluir_referencias, posicao_legenda, headerInfo);
            pdfMake.createPdf(docDefinition).getDataUrl((dataUrl) => {
                resolve(dataUrl);
            });
        } catch (err) {
            reject(err);
        }
    });
};

export const imprimirPdf = async (
    dados: typeEstruturaTodasAtvs[],
    incluir_insumos: boolean,
    incluir_patrimonios: boolean,
    incluir_referencias: boolean,
    posicao_legenda: 'Por célula' | 'Por tag' | 'Por referência',
    headerInfo?: HeaderDef
): Promise<void> => {
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    const docDefinition = gerarDocDefinition(dados, incluir_insumos, incluir_patrimonios, incluir_referencias, posicao_legenda, headerInfo);

    return new Promise((resolve, reject) => {
        try {
            pdfMake.createPdf(docDefinition).getBlob((blob) => {
                const blobUrl = URL.createObjectURL(blob);

                // 1. Salva o título original da página
                const originalTitle = document.title;

                // 2. Define o título temporário (usado como nome do arquivo na janela de impressão/salvamento)
                const fileTitle = headerInfo?.nome_arquivo || headerInfo?.titulo || 'Relatório';
                const tempTitle = fileTitle.replace(/[/\\?%*:|"<>]/g, '-');
                document.title = tempTitle;

                // 3. Cria um iframe oculto na página
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '1px';
                iframe.style.height = '1px';
                iframe.style.opacity = '0.01';
                iframe.style.pointerEvents = 'none';
                iframe.style.border = 'none';
                iframe.src = blobUrl;

                document.body.appendChild(iframe);

                let cleanedUp = false;
                const cleanUp = () => {
                    if (cleanedUp) return;
                    cleanedUp = true;

                    document.title = originalTitle;
                    URL.revokeObjectURL(blobUrl);
                    if (iframe.parentNode) {
                        document.body.removeChild(iframe);
                    }

                    // Remove ouvintes globais para evitar vazamento
                    window.removeEventListener('afterprint', cleanUp);
                    window.removeEventListener('focus', cleanUp);
                };

                // Ouvintes para restaurar o título original apenas quando o diálogo de impressão fechar
                window.addEventListener('afterprint', cleanUp);
                window.addEventListener('focus', cleanUp);

                iframe.onload = () => {
                    setTimeout(() => {
                        try {
                            iframe.contentWindow?.focus();
                            iframe.contentWindow?.print();

                            // Resolve a Promise imediatamente após disparar a janela de impressão
                            // Isso desativa o "loading" no botão sem travar a interface
                            resolve();
                        } catch (printErr) {
                            console.error("Erro ao invocar a impressão do iframe:", printErr);
                            cleanUp();
                            reject(printErr);
                        }
                    }, 500); // Pequeno atraso para garantir o carregamento do PDF no iframe
                };

                iframe.onerror = (err) => {
                    cleanUp();
                    reject(err);
                };
            });
        } catch (err) {
            reject(err);
        }
    });
};
